/**
 * Game - Main orchestrator for OneRealm
 * Coordinates all game systems, rendering, and UI
 */

import * as THREE from 'three';
import { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import { Renderer } from '@/rendering/Renderer';
import { SceneManager } from '@/rendering/SceneManager';
import { Camera } from '@/rendering/Camera';
import { Lights } from '@/rendering/Lights';
import { ChunkManager } from '@/rendering/terrain/ChunkManager';
import { WaterPlane } from '@/rendering/terrain/WaterPlane';
import { TileHighlight } from '@/rendering/terrain/TileHighlight';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import { UnitRenderer } from '@/rendering/units/UnitRenderer';
import { CavalryRenderer } from '@/rendering/units/CavalryRenderer';
import { ShieldBearerRenderer } from '@/rendering/units/ShieldBearerRenderer';
import { MageRenderer } from '@/rendering/units/MageRenderer';
import { ArcherRenderer } from '@/rendering/units/ArcherRenderer';
import { SwordsmanRenderer } from '@/rendering/units/SwordsmanRenderer';
import { SpearmanRenderer } from '@/rendering/units/SpearmanRenderer';
import { BuildingRenderer } from '@/rendering/buildings/BuildingRenderer';
import { DamageNumberRenderer } from '@/rendering/effects/DamageNumberRenderer';
import { HealthBarRenderer } from '@/rendering/effects/HealthBarRenderer';
import { ParticleSystem } from '@/rendering/effects/ParticleSystem';
import { FogRenderer } from '@/rendering/effects/FogRenderer';
import { MageSpellEffect } from '@/rendering/effects/MageSpellEffect';
import { ArcherArrowEffect } from '@/rendering/effects/ArcherArrowEffect';
import { SwordsmanSlashEffect } from '@/rendering/effects/SwordsmanSlashEffect';
import { SpearmanThrustEffect } from '@/rendering/effects/SpearmanThrustEffect';
import { CavalryChargeEffect } from '@/rendering/effects/CavalryChargeEffect';
import { CampfireEffect } from '@/rendering/effects/CampfireEffect';
import { FlagAnimation } from '@/rendering/effects/FlagAnimation';
import { ClickEffect } from '@/rendering/effects/ClickEffect';
import { MarchLineRenderer } from '@/rendering/effects/MarchLineRenderer';
import { InputManager } from '@/core/input/InputManager';
import { CameraController } from '@/core/input/CameraController';
import { FactionManager } from '@/game/factions/FactionManager';
import { MovementSystem } from '@/game/systems/MovementSystem';
import { CollisionSystem } from '@/game/systems/CollisionSystem';
import { PositionValidationSystem } from '@/game/systems/PositionValidationSystem';
import { CombatSystem } from '@/game/systems/CombatSystem';
import { SpawnSystem } from '@/game/systems/SpawnSystem';
import { TerritorySystem } from '@/game/systems/TerritorySystem';
import { EconomySystem } from '@/game/systems/EconomySystem';
import { RenderSyncSystem } from '@/game/systems/RenderSyncSystem';
import { SelectionSystem } from '@/game/systems/SelectionSystem';
import { VisionSystem } from '@/game/systems/VisionSystem';
import { AIController } from '@/game/ai/AIController';
import { HUD } from '@/ui/components/HUD';
import { BuildMenu } from '@/ui/components/BuildMenu';
import { Minimap } from '@/ui/components/Minimap';
import { FactionPanel } from '@/ui/components/FactionPanel';
import { EventLog } from '@/ui/components/EventLog';
import { SelectionInfo } from '@/ui/components/SelectionInfo';
import { DiplomacyPanel } from '@/ui/components/DiplomacyPanel';
import { TechPanel } from '@/ui/components/TechPanel';
import { GameOverScreen } from '@/ui/components/GameOverScreen';
import { SaveLoadMenu } from '@/ui/components/SaveLoadMenu';
import { SettingsPanel } from '@/ui/components/SettingsPanel';
import { MainMenu } from '@/ui/components/MainMenu';
import { UnitCountPanel } from '@/ui/components/UnitCountPanel';
import { HelpPanel } from '@/ui/components/HelpPanel';
import type { DifficultyLevel } from '@/game/ai/DifficultySettings';
import { DiplomacySystem } from '@/game/systems/DiplomacySystem';
import { TechSystem } from '@/game/tech/TechSystem';
import { audioManager } from '@/core/audio/AudioManager';
import { useGameStore } from '@/state/gameStore';
import { EventBus } from '@/core/events/EventBus';
import { FACTIONS } from '@/game/factions/FactionData';
import { wallConnectionSystem } from '@/game/systems/WallConnectionSystem';
import type { FactionId, GameConfig, Entity } from '@/types';
import { DEFAULT_CONFIG } from '@/types';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { BuildingComponent } from '@/game/components/Building';
import type { WallComponent } from '@/game/components/Wall';

// Managers
import { InputHandler } from '@/game/managers/InputHandler';
import { BuildingManager } from '@/game/managers/BuildingManager';
import { GameEventHandler } from '@/game/managers/GameEventHandler';
import { SaveLoadManager } from '@/game/managers/SaveLoadManager';
import { FactionSetup } from '@/game/managers/FactionSetup';
import { ALL_FACTIONS } from '@/game/managers/GameConstants';

export class Game {
  private config: GameConfig;
  private container: HTMLElement;

  // Core systems
  private world: World;
  private renderer: Renderer;
  private sceneManager: SceneManager;
  private camera: Camera;
  private lights: Lights;

  // Terrain
  private chunkManager: ChunkManager;
  private waterPlane: WaterPlane;
  private tileHighlight: TileHighlight;

  // Rendering
  private unitRenderer: UnitRenderer;
  private cavalryRenderer: CavalryRenderer;
  private shieldBearerRenderer: ShieldBearerRenderer;
  private mageRenderer: MageRenderer;
  private archerRenderer: ArcherRenderer;
  private swordsmanRenderer: SwordsmanRenderer;
  private spearmanRenderer: SpearmanRenderer;
  private buildingRenderer: BuildingRenderer;
  private damageNumberRenderer: DamageNumberRenderer;
  private healthBarRenderer: HealthBarRenderer;
  private particleSystem: ParticleSystem;
  private fogRenderer: FogRenderer;
  private mageSpellEffect: MageSpellEffect;
  private archerArrowEffect: ArcherArrowEffect;
  private swordsmanSlashEffect: SwordsmanSlashEffect;
  private spearmanThrustEffect: SpearmanThrustEffect;
  private cavalryChargeEffect: CavalryChargeEffect;
  private campfireEffect: CampfireEffect;
  private flagAnimation: FlagAnimation;
  private clickEffect: ClickEffect;
  private marchLineRenderer: MarchLineRenderer;

  // Input
  private inputManager: InputManager;
  private cameraController: CameraController;
  private inputHandler!: InputHandler;

  // Game logic
  private factionManager: FactionManager;
  private aiController: AIController;
  private territorySystem: TerritorySystem;
  private selectionSystem: SelectionSystem;
  private diplomacySystem: DiplomacySystem;
  private techSystem: TechSystem;
  private visionSystem: VisionSystem;

  // Managers
  private buildingManager!: BuildingManager;
  private saveLoadManager!: SaveLoadManager;
  private factionSetup!: FactionSetup;

  // UI
  private hud!: HUD;
  private buildMenu!: BuildMenu;
  private minimap!: Minimap;
  private factionPanel!: FactionPanel;
  private eventLog!: EventLog;
  private selectionInfo!: SelectionInfo;
  private diplomacyPanel!: DiplomacyPanel;
  private techPanel!: TechPanel;
  private gameOverScreen!: GameOverScreen;
  private saveLoadMenu!: SaveLoadMenu;
  private settingsPanel!: SettingsPanel;
  private mainMenu!: MainMenu;
  private unitCountPanel!: UnitCountPanel;
  private helpPanel!: HelpPanel;

  // State
  private difficulty: DifficultyLevel = 'normal';
  private isRunning: boolean = false;
  private lastTime: number = 0;
  private gameTime: number = 0;
  private lastHighlightedFaction: FactionId | null = null;

  constructor(container: HTMLElement, config: GameConfig = DEFAULT_CONFIG) {
    this.config = config;
    this.container = container;

    // Initialize core systems
    this.world = new World();
    this.renderer = new Renderer(container);
    this.sceneManager = new SceneManager();
    this.camera = new Camera();
    this.camera.setMapBounds(160);
    this.lights = new Lights(this.sceneManager.scene);

    // Initialize terrain
    this.chunkManager = new ChunkManager(this.sceneManager.scene, config);
    this.waterPlane = new WaterPlane();
    this.sceneManager.add(this.waterPlane.mesh);
    this.sceneManager.add(this.waterPlane.backgroundMesh);
    this.tileHighlight = new TileHighlight(this.sceneManager.scene);

    // Initialize renderers
    this.unitRenderer = new UnitRenderer(this.sceneManager.scene);
    this.cavalryRenderer = new CavalryRenderer(this.sceneManager.scene);
    this.shieldBearerRenderer = new ShieldBearerRenderer(this.sceneManager.scene);
    this.mageRenderer = new MageRenderer(this.sceneManager.scene);
    this.archerRenderer = new ArcherRenderer(this.sceneManager.scene);
    this.swordsmanRenderer = new SwordsmanRenderer(this.sceneManager.scene);
    this.spearmanRenderer = new SpearmanRenderer(this.sceneManager.scene);
    this.buildingRenderer = new BuildingRenderer(this.sceneManager.scene);
    this.damageNumberRenderer = new DamageNumberRenderer();
    this.healthBarRenderer = new HealthBarRenderer(this.sceneManager.scene);
    this.particleSystem = new ParticleSystem(this.sceneManager.scene);
    this.fogRenderer = new FogRenderer(this.sceneManager.scene);
    this.mageSpellEffect = new MageSpellEffect(this.sceneManager.scene);
    this.archerArrowEffect = new ArcherArrowEffect(this.sceneManager.scene);
    this.swordsmanSlashEffect = new SwordsmanSlashEffect(this.sceneManager.scene);
    this.spearmanThrustEffect = new SpearmanThrustEffect(this.sceneManager.scene);
    this.cavalryChargeEffect = new CavalryChargeEffect(this.sceneManager.scene);
    this.campfireEffect = new CampfireEffect(this.sceneManager.scene);
    this.flagAnimation = new FlagAnimation(this.sceneManager.scene);
    this.clickEffect = new ClickEffect(this.sceneManager.scene);
    this.marchLineRenderer = new MarchLineRenderer(this.sceneManager.scene);

    // Initialize input
    this.inputManager = new InputManager(this.renderer.domElement);
    this.cameraController = new CameraController(this.camera, this.inputManager);

    // Initialize game logic
    this.factionManager = new FactionManager();
    this.territorySystem = new TerritorySystem(config.tileSize);
    this.territorySystem.onTerritoryChange = (tileX, tileZ, newOwner) => {
      this.chunkManager.updateTerritoryColor(tileX, tileZ, newOwner);
    };

    this.diplomacySystem = new DiplomacySystem(this.factionManager);
    this.techSystem = new TechSystem(this.factionManager);
    this.visionSystem = new VisionSystem(this.factionManager);
    this.visionSystem.setDiplomacySystem(this.diplomacySystem);
    this.selectionSystem = new SelectionSystem();
    this.selectionSystem.setCamera(this.camera.camera);
    this.selectionSystem.setScene(this.sceneManager.scene);

    // Setup ECS systems
    this.setupECSSystems();

    // Initialize AI
    this.aiController = new AIController(this.world, this.factionManager, this.territorySystem);

    // Initialize wall connection system
    this.setupWallConnectionSystem();

    // Initialize UI
    this.initializeUI();

    // Initialize managers (after UI)
    this.initializeManagers();

    // Setup input handlers
    this.setupInputHandlers();
  }

  private setupECSSystems(): void {
    this.world.addSystem(new MovementSystem());
    this.world.addSystem(new CollisionSystem());
    this.world.addSystem(new PositionValidationSystem()); // Safety net for water boundary
    this.world.addSystem(new CombatSystem(this.factionManager));
    this.world.addSystem(new SpawnSystem(this.factionManager));
    this.world.addSystem(this.territorySystem);
    this.world.addSystem(this.diplomacySystem);
    this.world.addSystem(new EconomySystem(this.factionManager, this.territorySystem));

    const renderSyncSystem = new RenderSyncSystem(this.unitRenderer, this.buildingRenderer);
    renderSyncSystem.setCavalryRenderer(this.cavalryRenderer);
    renderSyncSystem.setShieldBearerRenderer(this.shieldBearerRenderer);
    renderSyncSystem.setMageRenderer(this.mageRenderer);
    renderSyncSystem.setArcherRenderer(this.archerRenderer);
    renderSyncSystem.setSwordsmanRenderer(this.swordsmanRenderer);
    renderSyncSystem.setSpearmanRenderer(this.spearmanRenderer);
    this.world.addSystem(renderSyncSystem);
    this.world.addSystem(this.visionSystem);
    this.world.addSystem(this.selectionSystem);
  }

  private setupWallConnectionSystem(): void {
    wallConnectionSystem.setGetWallComponent((entity) =>
      this.world.getComponent<WallComponent>(entity, ComponentTypes.Wall)
    );
    wallConnectionSystem.setOnVariantChange((entity, variant, rotation) => {
      this.buildingRenderer.updateWallVariant(entity, variant, rotation);
      EventBus.emit('wall:variant_changed', { entityId: entity, variant, rotation });
    });
  }

  private initializeUI(): void {
    this.hud = new HUD();
    this.buildMenu = new BuildMenu({
      onSelectBuilding: (type) => this.buildingManager?.setSelectedBuildingType(type),
      onCancel: () => this.buildingManager?.cancelBuildingPlacement(),
    });
    this.minimap = new Minimap();
    this.minimap.onClick = (x, z) => this.camera.setPosition(x, z);
    this.minimap.setTerritorySystem(this.territorySystem);
    this.factionPanel = new FactionPanel();
    this.eventLog = new EventLog();
    this.selectionInfo = new SelectionInfo();
    this.diplomacyPanel = new DiplomacyPanel();
    this.diplomacyPanel.setDiplomacySystem(this.diplomacySystem);
    this.diplomacyPanel.setFactionManager(this.factionManager);
    this.techPanel = new TechPanel();
    this.techPanel.setTechSystem(this.techSystem);
    this.techPanel.setFactionManager(this.factionManager);
    this.gameOverScreen = new GameOverScreen();
    this.gameOverScreen.setOnRestart(() => {
      this.dispose();
      window.location.reload();
    });
    this.gameOverScreen.setOnSpectate(() => {});
    this.saveLoadMenu = new SaveLoadMenu();
    this.settingsPanel = new SettingsPanel();
    this.mainMenu = new MainMenu();
    this.mainMenu.setCallbacks({
      onStartGame: (factionId, difficulty) => {
        this.difficulty = difficulty;
        this.startNewGame(factionId);
      },
    });
    this.unitCountPanel = new UnitCountPanel();
    this.helpPanel = new HelpPanel();
  }

  private initializeManagers(): void {
    this.buildingManager = new BuildingManager(
      this.world,
      this.sceneManager,
      this.tileHighlight,
      this.factionManager,
      this.eventLog
    );

    this.saveLoadMenu.setCallbacks({
      onSave: async (slot, name) => this.saveLoadManager.saveGame(slot, name),
      onLoad: (saveData) => this.saveLoadManager.loadGame(saveData),
      onReturnToMenu: () => this.returnToMainMenu(),
    });

    this.saveLoadManager = new SaveLoadManager(
      this.world,
      this.factionManager,
      this.techSystem,
      this.visionSystem,
      this.diplomacySystem,
      this.aiController,
      this.eventLog,
      this.fogRenderer,
      this.minimap,
      {
        getGameTime: () => this.gameTime,
        setGameTime: (time) => { this.gameTime = time; },
      }
    );

    this.factionSetup = new FactionSetup(
      this.world,
      this.factionManager,
      this.techSystem,
      this.visionSystem,
      this.aiController,
      this.camera,
      this.minimap,
      this.config
    );

    // Setup event handler
    new GameEventHandler(
      this.world,
      this.eventLog,
      this.minimap,
      this.camera,
      this.campfireEffect,
      this.flagAnimation,
      this.buildingManager
    );
  }

  private setupInputHandlers(): void {
    this.inputHandler = new InputHandler({
      onToggleFactionPanel: () => this.factionPanel.toggle(),
      onToggleDiplomacyPanel: () => this.diplomacyPanel.toggle(),
      onToggleTechPanel: () => this.techPanel.toggle(),
      onToggleHelpPanel: () => this.helpPanel.toggle(),
      onToggleFogOfWar: () => this.fogRenderer.toggle(),
      onToggleSettingsPanel: () => this.settingsPanel.toggle(),
      onToggleSaveLoadMenu: () => this.saveLoadMenu.toggle('save'),
      onDemolishBuildings: () => this.buildingManager.demolishSelectedBuildings(),
      onDeselectBuilding: () => this.buildingManager.deselectBuilding(),
      onCancelBuildingPlacement: () => this.buildingManager.cancelBuildingPlacement(),
      onQuickSave: () => this.saveLoadManager.quickSave(),
      onQuickLoad: () => this.saveLoadManager.quickLoad(),
      onSetControlGroup: (groupNum) => this.selectionSystem.setControlGroup(groupNum),
      onSelectControlGroup: (groupNum) => this.selectionSystem.selectControlGroup(groupNum),
      isSettingsPanelVisible: () => this.settingsPanel.isVisible(),
      isSaveLoadMenuVisible: () => this.saveLoadMenu.isVisible(),
      hasSelectedBuildings: () => this.buildingManager.hasSelectedBuildings(),
      hasSelectedBuildingType: () => this.buildingManager.hasSelectedBuildingType(),
    });

    this.inputManager.onClick = (screenX, screenY, button) => {
      this.handleClick(screenX, screenY, button);
    };

    this.inputManager.onRightClick = (screenX, screenY) => {
      this.handleRightClick(screenX, screenY);
    };

    this.inputManager.onDragStart = (screenX, screenY, button) => {
      if (button === 0 && !this.buildingManager.hasSelectedBuildingType()) {
        this.selectionSystem.startBoxSelection(screenX, screenY);
      }
    };

    this.inputManager.onDragMove = (screenX, screenY) => {
      this.selectionSystem.updateBoxSelection(screenX, screenY);
    };

    this.inputManager.onDragEnd = () => {
      this.selectionSystem.endBoxSelection((x, y) => this.cameraController.getWorldPosition(x, y));
    };
  }

  private handleClick(screenX: number, screenY: number, button: number): void {
    const worldPos = this.cameraController.getWorldPosition(screenX, screenY);
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    if (button === 0 && this.buildingManager.hasSelectedBuildingType()) {
      this.buildingManager.placeBuildingAt(worldPos, playerFaction);
      return;
    }

    if (button === 0) {
      const clickedBuilding = this.buildingManager.getBuildingAtPosition(worldPos, playerFaction);
      const isCtrlPressed = this.inputManager.isKeyDown('ControlLeft') || this.inputManager.isKeyDown('ControlRight');

      if (clickedBuilding !== null) {
        this.buildingManager.selectBuilding(clickedBuilding, isCtrlPressed);
        return;
      } else if (!isCtrlPressed && this.buildingManager.hasSelectedBuildings()) {
        this.buildingManager.deselectBuilding();
      }
    }

    if (button === 0) {
      this.selectionSystem.handleClick(screenX, screenY, button, worldPos);
    }
  }

  private handleRightClick(screenX: number, screenY: number): void {
    const worldPos = this.cameraController.getWorldPosition(screenX, screenY);
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    if (this.buildingManager.hasSelectedBuildingType()) {
      this.buildingManager.cancelBuildingPlacement();
      return;
    }

    this.selectionSystem.handleClick(screenX, screenY, 2, worldPos);
  }

  showMainMenu(): void {
    this.mainMenu.show();
    audioManager.init().then(() => {
      audioManager.playMusic('menu');
    });
  }

  returnToMainMenu(): void {
    this.isRunning = false;
    this.world.clear();
    this.unitRenderer.clear();
    this.buildingRenderer.clear();
    this.territorySystem.clear();
    tileOccupancy.clear();
    wallConnectionSystem.clear();
    this.factionSetup.clear();
    this.buildingManager.clear();
    useGameStore.getState().reset();
    useGameStore.getState().setPhase('menu');
    this.showMainMenu();
  }

  startNewGame(playerFaction: FactionId = 'lin'): void {
    this.mainMenu.hide();

    useGameStore.getState().reset();
    useGameStore.getState().setPlayerFaction(playerFaction);
    useGameStore.getState().setDifficulty(this.difficulty);
    useGameStore.getState().setPhase('playing');

    this.factionManager.setPlayerFaction(playerFaction);

    tileOccupancy.setPlayerFaction(playerFaction);
    tileOccupancy.setTerritoryCheck((wx, wz) => this.territorySystem.getTileOwner(wx, wz));
    tileOccupancy.setUnitCheck((tileX, tileZ) => {
      const units = this.world.query([ComponentTypes.Position, ComponentTypes.Unit]);
      for (const entity of units) {
        const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
        if (pos && Math.floor(pos.x) === tileX && Math.floor(pos.z) === tileZ) {
          return true;
        }
      }
      return false;
    });

    this.visionSystem.setPlayerFaction(playerFaction);
    this.visionSystem.initFaction(playerFaction);
    this.fogRenderer.setVisionSystem(this.visionSystem);
    this.fogRenderer.setPlayerFaction(playerFaction);

    this.damageNumberRenderer.setCamera(this.camera);
    this.damageNumberRenderer.setWorld(this.world);
    this.healthBarRenderer.setCamera(this.camera);
    this.healthBarRenderer.setWorld(this.world);
    this.particleSystem.setWorld(this.world);
    this.marchLineRenderer.setWorld(this.world);
    this.selectionInfo.setWorld(this.world);
    this.unitCountPanel.setWorld(this.world);

    for (const factionId of Object.keys(FACTIONS) as FactionId[]) {
      if (factionId !== playerFaction && factionId !== 'neutral') {
        this.factionManager.setRelation(playerFaction, factionId, -50);
      }
    }

    this.factionSetup.setupFactions(playerFaction);
    this.territorySystem.forceUpdate();
    this.saveLoadManager.setupAutoSave();

    audioManager.init().then(() => {
      audioManager.playMusic('peaceful');
    });

    this.isRunning = true;
    this.lastTime = performance.now();
    this.gameLoop();
  }

  private gameLoop(): void {
    if (!this.isRunning) return;

    requestAnimationFrame(() => this.gameLoop());

    const currentTime = performance.now();
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1);
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.render();
  }

  private update(deltaTime: number): void {
    const gameSpeed = useGameStore.getState().gameSpeed;
    const scaledDelta = deltaTime * gameSpeed;

    this.gameTime += scaledDelta;
    useGameStore.getState().setGameTime(this.gameTime);

    this.cameraController.update(deltaTime);
    this.chunkManager.update(this.camera.target.x, this.camera.target.z);
    this.world.update(scaledDelta);
    this.aiController.update(scaledDelta);
    this.techSystem.update(scaledDelta);
    this.techPanel.update();

    // Update visual effects
    this.updateVisualEffects(deltaTime, scaledDelta);

    // Update building highlight
    const pointerPos = this.inputManager.getPointerPosition();
    this.buildingManager.updateBuildingHighlight(
      pointerPos.x,
      pointerPos.y,
      (x, y) => this.cameraController.getWorldPosition(x, y)
    );

    this.lights.updateShadowCamera(this.camera.target.x, this.camera.target.z);
    this.updateUIState();
    this.updateMinimap();
    this.checkGameEnd();
  }

  private updateVisualEffects(deltaTime: number, scaledDelta: number): void {
    this.waterPlane.update(deltaTime);
    this.cavalryRenderer.update(deltaTime);
    this.shieldBearerRenderer.update(deltaTime);
    this.mageRenderer.update(deltaTime);
    this.archerRenderer.update(deltaTime);
    this.swordsmanRenderer.update(deltaTime);
    this.spearmanRenderer.update(deltaTime);
    this.buildingRenderer.update(deltaTime);
    this.damageNumberRenderer.update(deltaTime);
    this.healthBarRenderer.update();
    this.particleSystem.update(deltaTime);
    this.fogRenderer.update(scaledDelta);
    this.mageSpellEffect.update(deltaTime);
    this.archerArrowEffect.update(deltaTime);
    this.swordsmanSlashEffect.update(deltaTime);
    this.spearmanThrustEffect.update(deltaTime);
    this.cavalryChargeEffect.update(deltaTime);
    this.campfireEffect.update(deltaTime);
    this.flagAnimation.update(deltaTime);
    this.clickEffect.update(deltaTime);
    this.buildingManager.updateSelectionRings();
    this.marchLineRenderer.update(deltaTime, this.selectionSystem.getSelectedUnits());
    this.lights.update(deltaTime);
    this.tileHighlight.update(deltaTime);
  }

  private updateUIState(): void {
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const state = this.factionManager.getFactionState(playerFaction);
    if (state) {
      useGameStore.getState().setPlayerGold(state.gold);
      useGameStore.getState().setStats(state.unitCount, state.buildingCount, state.territoryCount);
    }

    this.updateFactionPanel();
    this.updateTerritoryHighlight();
  }

  private updateTerritoryHighlight(): void {
    const highlightedFaction = useGameStore.getState().highlightedFaction;
    if (highlightedFaction === this.lastHighlightedFaction) return;

    this.lastHighlightedFaction = highlightedFaction;
    this.chunkManager.setHighlightedFaction(highlightedFaction);

    const allTiles = this.territorySystem.getAllTiles();
    for (const tile of allTiles) {
      this.chunkManager.updateTerritoryColor(tile.x, tile.z, tile.owner);
    }
  }

  private updateFactionPanel(): void {
    const playerFaction = useGameStore.getState().playerFaction;

    for (const factionId of ALL_FACTIONS) {
      const state = this.factionManager.getFactionState(factionId);
      const relationToPlayer = playerFaction
        ? this.factionManager.getRelation(factionId, playerFaction)
        : 0;

      this.factionPanel.updateFactionStatus(factionId, {
        gold: state?.gold || 0,
        unitCount: state?.unitCount || 0,
        buildingCount: state?.buildingCount || 0,
        territoryCount: state?.territoryCount || 0,
        relationToPlayer,
        isAlive: !state?.isDefeated && (state?.territoryCount || 0) > 0,
      });
    }
  }

  private updateMinimap(): void {
    const units: { x: number; z: number; factionId: FactionId }[] = [];
    const buildings: { x: number; z: number; factionId: FactionId; isCapital: boolean }[] = [];

    const unitEntities = this.world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Unit]);
    for (const entity of unitEntities) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      units.push({ x: pos.x, z: pos.z, factionId: faction.factionId });
    }

    const buildingEntities = this.world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Building]);
    for (const entity of buildingEntities) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      buildings.push({
        x: pos.x,
        z: pos.z,
        factionId: faction.factionId,
        isCapital: building.buildingType === 'capital',
      });
    }

    const frustum = this.camera.getFrustumSize();
    this.minimap.updateCamera(this.camera.target.x, this.camera.target.z, frustum.width, frustum.height);
    this.minimap.render(units, buildings);
  }

  private checkGameEnd(): void {
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const currentPhase = useGameStore.getState().phase;
    if (currentPhase === 'victory' || currentPhase === 'defeat') return;
    if (this.gameTime < 2.0) return;

    const playerState = this.factionManager.getFactionState(playerFaction);
    if (!playerState || playerState.territoryCount === 0) {
      useGameStore.getState().setPhase('defeat');
      this.eventLog.addEvent('战败', '我方已失去所有领地，国家灭亡...', 'defeat');
      audioManager.playMusic('defeat');
      audioManager.playSFX('defeat');
      return;
    }

    const activeFactionIds = this.factionSetup.getActiveFactionIds();
    const aiFactions = activeFactionIds.filter((f) => f !== playerFaction);

    for (const factionId of aiFactions) {
      if (this.factionSetup.isFactionDefeated(factionId)) continue;

      const state = this.factionManager.getFactionState(factionId);
      if (!state || state.territoryCount === 0) {
        this.factionSetup.markFactionDefeated(factionId);
        this.factionManager.setDefeated(factionId);
        this.territorySystem.setFactionDefeated(factionId);
        this.eventLog.notifyDefeat(factionId);
      }
    }

    const remainingAI = aiFactions.filter(f => !this.factionSetup.isFactionDefeated(f));
    if (remainingAI.length === 0 && aiFactions.length > 0) {
      useGameStore.getState().setPhase('victory');
      useGameStore.getState().setWinner(playerFaction);
      this.eventLog.notifyVictory(playerFaction);
      audioManager.playMusic('victory');
      audioManager.playSFX('victory');
    }
  }

  private render(): void {
    this.renderer.render(this.sceneManager.scene, this.camera.camera);
  }

  pause(): void {
    this.isRunning = false;
    useGameStore.getState().setPhase('paused');
  }

  resume(): void {
    if (useGameStore.getState().phase === 'paused') {
      this.isRunning = true;
      this.lastTime = performance.now();
      useGameStore.getState().setPhase('playing');
      this.gameLoop();
    }
  }

  dispose(): void {
    this.isRunning = false;
    this.saveLoadManager.dispose();
    audioManager.dispose();

    this.hud.dispose();
    this.buildMenu.dispose();
    this.minimap.dispose();
    this.factionPanel.dispose();
    this.eventLog.dispose();
    this.selectionInfo.dispose();
    this.diplomacyPanel.dispose();
    this.techPanel.dispose();
    this.gameOverScreen.dispose();
    this.saveLoadMenu.dispose();
    this.settingsPanel.dispose();
    this.mainMenu.dispose();
    this.unitCountPanel.dispose();
    this.helpPanel.dispose();

    this.inputManager.dispose();
    this.cameraController.dispose();
    this.inputHandler.dispose();
    this.selectionSystem.dispose();
    this.buildingManager.dispose();

    this.unitRenderer.dispose();
    this.cavalryRenderer.dispose();
    this.shieldBearerRenderer.dispose();
    this.mageRenderer.dispose();
    this.archerRenderer.dispose();
    this.swordsmanRenderer.dispose();
    this.spearmanRenderer.dispose();
    this.buildingRenderer.dispose();
    this.damageNumberRenderer.dispose();
    this.healthBarRenderer.dispose();
    this.particleSystem.dispose();
    this.fogRenderer.dispose();
    this.mageSpellEffect.dispose();
    this.archerArrowEffect.dispose();
    this.swordsmanSlashEffect.dispose();
    this.spearmanThrustEffect.dispose();
    this.cavalryChargeEffect.dispose();
    this.campfireEffect.dispose();
    this.flagAnimation.dispose();
    this.clickEffect.dispose();
    this.marchLineRenderer.dispose();
    this.tileHighlight.dispose();
    tileOccupancy.clear();
    wallConnectionSystem.clear();
    this.chunkManager.clear();
    this.waterPlane.dispose();

    this.renderer.dispose();
    this.world.clear();

    EventBus.clear();
  }
}
